import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Adapts a single backend module (or unit-aware response) to frontend shape.
 * Single module endpoint may return richer data including units array.
 */
function adaptSingleModule(m) {
  const pct = m.progress?.percent ?? 0;
  let status = 'not_started';
  if (!m.is_unlocked) {
    status = 'locked';
  } else if (pct >= 100) {
    status = 'completed';
  } else if (pct > 0) {
    status = 'in_progress';
  }

  return {
    id: m.module_id || m.id || m.slug,
    module_id: m.module_id,
    slug: m.slug,
    order: m.order,
    title: m.title,
    subtitle: m.subtitle || '',
    description: m.description || '',
    emoji: m.icon_emoji || m.emoji || '📚',
    gradient: m.color_gradient || m.gradient || 'from-sky-500 to-blue-600',
    status: m.status || status,
    progress: pct,
    estimatedTime: m.estimated_hours ? `${m.estimated_hours}` : (m.estimatedTime || '1'),
    unitCount: m.total_units ?? m.unitCount ?? 0,
    lessonCount: m.total_lessons ?? m.lessonCount ?? 0,
    milesReward: m.milesReward || m.miles_reward || 0,
    is_unlocked: m.is_unlocked ?? true,
    // units array if returned by backend
    units: m.units || [],
    completed_lessons: m.progress?.completed_lessons ?? 0,
    total_lessons_count: m.progress?.total_lessons ?? m.total_lessons ?? 0,
  };
}

async function fetchCabinModule(slug, lang = 'tr') {
  const token = getToken();
  const res = await axios.get(`${API}/cabin/modules/${slug}?lang=${lang}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  // Backend may wrap in { module: {...} } or return directly
  const data = res.data?.module ?? res.data;
  return adaptSingleModule(data);
}

export function useCabinModule(slug) {
  const { lang } = useLanguage();
  return useQuery({
    queryKey: ['cabin', 'module', slug, lang],
    queryFn: () => fetchCabinModule(slug, lang),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!slug && typeof window !== 'undefined' && !!getToken(),
  });
}
